import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSupportContext } from '../context/SupportContext';
import axios from 'axios';
import {
  Building2,
  Users,
  LayoutDashboard,
  Activity,
  Settings,
  LogOut,
  Shield,
  Command,
  FileWarning,
  Crown,
  Zap,
  Sun,
  Moon,
  Monitor,
  Contact,
  MessageSquare,
  Target,
  Sparkles,
  CheckSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ContextSwitcher from '../components/platform/ContextSwitcher';
import CommandPalette from '../components/platform/CommandPalette';
import SupportModeBanner from '../components/platform/SupportModeBanner';
import { cn } from '../lib/utils';
import FundEliteBrandMark from '../components/branding/FundEliteBrandMark';
import TaskReminderProvider from '../features/tasks/components/TaskReminderProvider';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Platform Owner Console Layout - Phase 8 + Phase 9 Enhancements
 * 
 * Main shell for platform owner admin console.
 * Includes:
 * - Context Switcher in sidebar
 * - Command Palette (Cmd+K / Ctrl+K)
 * - Premium support mode banner
 * - Appearance preference toggle
 */
const PlatformOwnerLayout = () => {
  const { user, token, logout } = useAuth();
  const { isInSupportMode } = useSupportContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [appearancePreference, setAppearancePreference] = useState('dark');

  // Fetch user preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/platform/automations/users/me/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.appearance_preference) {
          setAppearancePreference(res.data.appearance_preference);
          applyTheme(res.data.appearance_preference);
        }
      } catch (err) {
        // Defaults to dark
      }
    };
    fetchPreferences();
  }, [token]);

  // Apply theme
  const applyTheme = (theme) => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.remove('dark');
      root.classList.add('light');
    } else if (theme === 'dark') {
      root.classList.remove('light');
      root.classList.add('dark');
    } else {
      // System preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.remove('light');
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
        root.classList.add('light');
      }
    }
  };

  // Handle theme change
  const handleThemeChange = async (theme) => {
    setAppearancePreference(theme);
    applyTheme(theme);
    
    // Save preference
    try {
      await axios.put(`${API}/platform/automations/users/me/preferences`, {
        appearance_preference: theme
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to save appearance preference');
    }
  };

  // Global keyboard shortcut for Command Palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/platform/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/platform/unified-inbox', label: 'Unified Inbox', icon: MessageSquare },
    { path: '/platform/opportunities', label: 'Opportunities', icon: Target },
    { path: '/platform/fundgenius-hq', label: 'FundGenius HQ', icon: Sparkles, badge: 'New' },
    { path: '/platform/agencies', label: 'Agencies', icon: Building2 },
    { path: '/platform/contacts', label: 'Contacts', icon: Contact },
    { path: '/platform/users', label: 'Users', icon: Users },
    { path: '/platform/activity', label: 'Activity', icon: Activity },
    { path: '/platform/automations', label: 'Automations', icon: Zap },
    { path: '/platform/tasks', label: 'Tasks', icon: CheckSquare, moduleKey: 'tasks' },
    { path: '/platform/settings', label: 'Settings', icon: Settings },
  ];

  const isActive = (path) => location.pathname.startsWith(path);
  const isAutomationsShell = location.pathname.startsWith('/platform/automations');
  // Tasks must sit flush on the shell background — no padded light page wrapper.
  const isTasksShell = location.pathname.startsWith('/platform/tasks');
  const taskReminderWorkspace = useMemo(() => ({
    scopeType: 'platform',
    platformId: user?.platform_id || null,
    agencyId: null,
    timezone: user?.timezone || 'UTC',
    permissions: user?.permissions || [],
  }), [user]);

  return (
    <div className="min-h-screen bg-stone-900">
      <TaskReminderProvider workspace={taskReminderWorkspace} />
      {/* Support Mode Banner - Premium Glassmorphism */}
      <SupportModeBanner />

      {/* Command Palette */}
      <CommandPalette 
        isOpen={isCommandPaletteOpen} 
        onClose={() => setIsCommandPaletteOpen(false)} 
      />

      <div className="flex min-w-0">
        {/* Sidebar */}
        <aside className="w-64 flex-shrink-0 min-h-screen bg-gradient-to-b from-stone-800 to-stone-900 border-r border-slate-700 flex flex-col sticky top-0 h-screen overflow-hidden z-30">
          {/* Logo */}
          <div className="p-6 border-b border-slate-700 flex-shrink-0">
            <div className="flex flex-col gap-1" data-testid="platform-sidebar-logo">
              <FundEliteBrandMark
                variant="agency-admin"
                mode="logo"
                logoClassName="h-8 w-auto max-w-[168px] object-contain object-left"
              />
              <p className="text-xs text-slate-400">Platform Console</p>
            </div>
          </div>

          {/* Context Switcher - Phase 9 */}
          <div className="p-4 border-b border-slate-700/50 flex-shrink-0">
            <ContextSwitcher variant="platform" />
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`platform-nav-${item.label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all ${
                        active
                          ? 'bg-emerald-600 text-white'
                          : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                      }`}
                    >
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                      {item.badge ? (
                        <span className="ml-auto rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                          {item.badge}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}

                {/* Command Palette Trigger */}
                <button
                  onClick={() => setIsCommandPaletteOpen(true)}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all mt-4 border border-slate-700/50"
                  data-testid="cmd-k-trigger"
                >
                  <div className="flex items-center gap-3">
                    <Command size={18} />
                    <span className="text-sm">Quick Search</span>
                  </div>
                  <kbd className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">⌘K</kbd>
                </button>

                {/* Admin Control Center Link - Only for authorized internal roles */}
                {['platform_owner', 'platform_admin', 'admin', 'owner', 'specialist'].includes(user?.role) && (
                  <div className="mt-6 pt-4 border-t border-slate-700/50">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 px-2">Internal Tools</p>
                    
                    {/* Collection Letter Markup */}
                    <Link
                      to="/staff/collection-markup"
                      data-testid="platform-nav-letter-markup"
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all mb-2 ${
                        location.pathname.includes('collection-markup')
                          ? 'bg-emerald-600/30 text-emerald-300 border border-emerald-500/50'
                          : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                      }`}
                    >
                      <FileWarning size={18} />
                      <span className="font-medium">Letter Markup</span>
                    </Link>
                    
                    <Link
                      to="/admin"
                      data-testid="platform-nav-admin-control"
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all border ${
                        location.pathname.startsWith('/admin')
                          ? 'bg-amber-600/30 text-amber-300 border-amber-500/50 shadow-lg shadow-amber-500/10'
                          : 'bg-gradient-to-r from-amber-600/15 to-amber-600/15 text-amber-400 hover:from-amber-600/25 hover:to-amber-600/25 border-amber-500/30'
                      }`}
                    >
                      <div className="p-1.5 rounded-md bg-amber-500/20">
                        <Shield size={18} />
                      </div>
                      <div className="flex-1">
                        <span className="font-medium block">Admin Control</span>
                        <span className="text-xs text-slate-500">AI Operations & Training</span>
                      </div>
                    </Link>
                  </div>
                )}
              </nav>

              {/* User Card - Fixed at bottom */}
              <div className="p-4 border-t border-slate-700 flex-shrink-0">
            {/* Appearance Toggle */}
            <div className="flex items-center justify-between mb-3 p-2 rounded-lg bg-slate-800/50">
              <span className="text-xs text-slate-400">Appearance</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleThemeChange('light')}
                  className={cn(
                    'p-1.5 rounded-md transition-colors',
                    appearancePreference === 'light'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'text-slate-500 hover:text-slate-300'
                  )}
                  title="Light"
                >
                  <Sun size={14} />
                </button>
                <button
                  onClick={() => handleThemeChange('dark')}
                  className={cn(
                    'p-1.5 rounded-md transition-colors',
                    appearancePreference === 'dark'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'text-slate-500 hover:text-slate-300'
                  )}
                  title="Dark"
                >
                  <Moon size={14} />
                </button>
                <button
                  onClick={() => handleThemeChange('system')}
                  className={cn(
                    'p-1.5 rounded-md transition-colors',
                    appearancePreference === 'system'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'text-slate-500 hover:text-slate-300'
                  )}
                  title="System"
                >
                  <Monitor size={14} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0) || 'P'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Platform Owner'}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <LogOut size={16} className="mr-2" />
              Sign Out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main
          className={cn(
            'flex-1 min-h-screen min-w-0 overflow-x-hidden',
            isTasksShell
              ? 'bg-background'
              : isAutomationsShell
                ? 'bg-stone-900'
                : 'p-8 bg-slate-50'
          )}
          data-testid={isTasksShell ? 'platform-tasks-shell' : undefined}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PlatformOwnerLayout;
